import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN, LOCATION_ADMIN } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

const CAN_CONFIG_DATA_PRIVACY_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN];

// Any of these permissions are sufficient to enable Settings in the left nav
const CAN_VISIT_SETTINGS_AUTHZ_PERMISSIONS = [
  Permission.VISITORS_BLOCKLIST_READ,
  Permission.VISITORS_WATCHLIST_READ,
  Permission.VISITORS_FULL_LEGAL_NAME_SETTING_READ,
  Permission.VISITORS_LOCATION_CONFIG_UPDATE_HOST_NOTIFICATIONS,
  Permission.VISITORS_LOCATION_CONFIG_UPDATE_INVITES,
  Permission.VISITORS_LOCATION_CONFIG_UPDATE_SECURITY_DESK_LINK,
  Permission.VISITORS_FALLBACK_CONTACT_READ,
  Permission.VISITORS_FLOW_INSPECT,
];

export default class SettingAbility extends Ability {
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  isProtect = false;

  get canVisit(): boolean {
    const permissions = [...CAN_VISIT_SETTINGS_AUTHZ_PERMISSIONS];

    if (this.featureFlags.isEnabled('visitors-permission-export-visitor-history')) {
      permissions.push(Permission.VISITORS_ENTRY_EXPORT_ALL);
    }

    if (this.featureFlags.isEnabled('visitors-permission-emails-and-guides')) {
      permissions.push(
        Permission.VISITORS_LOCATION_CONFIGURATION_MANAGE_EMAIL,
        Permission.VISITORS_MAILER_TEMPLATE_MAPPING_CREATE,
        Permission.VISITORS_MAILER_TEMPLATE_MAPPING_DELETE,
        Permission.VISITORS_MAILER_TEMPLATE_MAPPING_UPDATE,
        Permission.VISITORS_VISITOR_GUIDE_SECTION_UPDATE,
        Permission.VISITORS_VISITOR_SURVEY_CONFIGURATION_UPDATE,
      );
    }

    return this.authz.hasAnyPermissionAtCurrentLocation(permissions);
  }

  get canConfigureDataPrivacy(): boolean {
    if (!this.featureFlags.isEnabled('visitors-permission-manage-location-settings')) {
      const { roleNames } = this.currentAdmin;
      return isPresent(_intersection(CAN_CONFIG_DATA_PRIVACY_ROLES, roleNames));
    }

    return this.authz.hasAnyPermissionAtCurrentLocation([
      Permission.VISITORS_GDPR_CONFIGURATION_UPDATE,
      Permission.VISITORS_GDPR_CONFIGURATION_CREATE,
      Permission.VISITORS_GDPR_CONFIGURATION_DELETE,
    ]);
  }

  get canManageResponseOptions(): boolean {
    const isPremiumOrEnterprisePlan =
      this.state.vrSubscription?.isPremiumPlan || this.state.vrSubscription?.isEnterprisePlan || false;
    const { roleNames } = this.currentAdmin;

    if (!isPremiumOrEnterprisePlan) return false;
    if (this.isProtect) return roleNames.includes(GLOBAL_ADMIN);
    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_FLOW_INSPECT);
  }
}
